// ── Session invite-log section (append-only read, status-only) ──────────────
// Wave 6 (Sessions). F5: the invite log is an append-only per-attempt trigger log
// (no provider payloads, emails, join URLs, or passcodes — the DTO pins
// rawPayloadReturned / secretReturned to false). This panel renders the per-attempt
// outcomes only (channel / attempt / outcome / time), never a recipient email or
// a provider secret. Until any invite is triggered the log is empty -> honest
// "no invites triggered yet".
//
// The Z6 read hook is live-only; in mock mode it returns data:null with
// isLiveBound:false -> the honest "not yet available" status (not an error).

import { Alert, Badge, Module, ModuleBody, ModuleHead, SkeletonRows, humanizeLabel } from "@vjal/ui";

import { useCoachingInviteLog } from "../../../hooks/useCoachingZ6Data";

export function SessionInviteLogSection({ sessionId }: { sessionId: string }) {
  const binding = useCoachingInviteLog(sessionId);
  const entries = binding.data?.inviteLog?.entries ?? [];

  return (
    <Module span={6}>
      <ModuleHead title="Invite log" subtitle="Per-attempt invite triggers — no emails, join links, or secrets." />
      <ModuleBody className="space-y-3">
        {binding.denied ? (
          <div data-honest-posture="permission-denied">
            <Alert variant="info">Your role can't view the invite log for this session.</Alert>
          </div>
        ) : binding.isError ? (
          <div data-honest-posture="unavailable">
            <Alert variant="error">
              The invite log is temporarily unavailable{binding.reference ? ` (reference: ${binding.reference})` : ""}.
            </Alert>
          </div>
        ) : binding.isLoading ? (
          <div data-honest-posture="loading" aria-label="Loading invite log">
            <SkeletonRows rows={2} columns={2} className="rounded-lg border border-line" />
          </div>
        ) : entries.length === 0 ? (
          <div data-honest-posture="unavailable">
            <Alert variant="info">No invites have been triggered for this session yet.</Alert>
          </div>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0" data-honest-posture-ready="invite-log">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-center justify-between gap-3 text-sm text-ink-body">
                <span>
                  {humanizeLabel(entry.channel)} · attempt {entry.attempt}
                </span>
                <Badge variant={entry.outcome === "succeeded" ? "success" : "warning"}>{humanizeLabel(entry.outcome)}</Badge>
              </li>
            ))}
          </ul>
        )}
      </ModuleBody>
    </Module>
  );
}
